import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { PasswordInput } from "../components/PasswordInput";
import { Button } from "../components/ui/button";
import { Loader2, ArrowLeft, KeyRound, CheckCircle, AlertTriangle } from "lucide-react";
import AmmoLogo from "../components/AmmoLogo";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** FastAPI's error detail, which is a string for our own errors and a list of
 *  objects when validation rejects the body. The fallback is passed in rather
 *  than written here, because this file has no business holding an English
 *  sentence of its own. */
function formatApiErrorDetail(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || fallback;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return fallback;
}

/** The half of the screen that is only decoration, hidden below lg. Written
 *  once rather than pasted into each of this page's three states. */
function Brand() {
  return (
    <div className="hidden lg:flex lg:w-1/2 relative bg-indigo-800">
      <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
        <div className="w-28 h-28 rounded-full bg-white flex items-center justify-center shadow-lg mb-8">
          <AmmoLogo className="w-20 h-20" />
        </div>
        <h1 className="text-4xl font-bold text-white text-center">AMMO Core</h1>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const { t, language, setLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) return;
    setError("");
    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      // Unlike the request page, the answer here reveals nothing about who has
      // an account: the token either works or it does not, and somebody
      // holding one already has it. An expired link needs to say so, or the
      // only way to find out is that nothing happens.
      setError(formatApiErrorDetail(err.response?.data?.detail, t("common.actionFailed")));
    } finally {
      setLoading(false);
    }
  };

  // No token in the link
  if (!token) {
    return (
      <div className="min-h-screen flex">
        <Brand />
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-3" data-testid="reset-invalid-title">
              {t("reset.invalidTitle")}
            </h2>
            <p className="text-slate-500 mb-8" data-testid="reset-invalid-message">
              {t("reset.invalidMessage")}
            </p>
            <Link
              to="/forgot-password"
              data-testid="reset-request-new-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              {t("reset.requestNew")}
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex">
        <Brand />
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-3" data-testid="reset-success-title">
              {t("reset.successTitle")}
            </h2>
            <p className="text-slate-500 mb-8" data-testid="reset-success-message">
              {t("reset.successMessage")}
            </p>
            <Link
              to="/login"
              data-testid="reset-go-to-login-btn"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-indigo-700 hover:bg-indigo-600 text-white font-medium rounded-lg transition-colors"
            >
              {t("reset.goToLogin")}
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  const canSubmit = password && confirmPassword && password === confirmPassword && !loading;

  return (
    <div className="min-h-screen flex">
      <Brand />
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <Link
            to="/login"
            data-testid="reset-back-link"
            className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("forgot.backToLogin")}
          </Link>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
              <KeyRound className="w-5 h-5 text-indigo-600" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">{t("reset.title")}</h2>
          </div>
          <p className="text-slate-500 mb-8">{t("reset.subtitle")}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <PasswordInput
              password={password}
              setPassword={setPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              required
            />

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm break-words"
                data-testid="reset-error"
              >
                {error}
              </motion.div>
            )}

            <Button
              type="submit"
              data-testid="reset-submit-btn"
              disabled={!canSubmit}
              className="w-full h-11 bg-indigo-700 hover:bg-indigo-600 text-white font-medium rounded-lg"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("reset.submit")}
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-400">{t("reset.hint")}</p>
          <div className="flex justify-center gap-2 mt-4">
            <button onClick={() => setLanguage("en")} className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${language === "en" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>EN</button>
            <button onClick={() => setLanguage("it")} className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${language === "it" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>IT</button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
